<script setup lang="ts">
import { RBCGroupedCategory } from '@/types/rbc';
import ProgressBar from './ProgressBar.vue';
import StatusBadge from './StatusBadge.vue';
import { getStatusTheme } from '@/utils/progressUtils';

defineProps<{
  categories: RBCGroupedCategory[];
}>();
</script>

<template>
  <div class="table-responsive shadow-sm border overflow-hidden">
    <table class="table table-hover mb-0 align-middle">
      <thead class="bg-light">
        <tr>
          <th class="ps-4 py-3 text-uppercase text-muted small fw-bold">
            Cell Type
          </th>
          <th class="py-3 text-uppercase text-muted small fw-bold text-end">
            Labeled Count
          </th>
          <th class="py-3 text-uppercase text-muted small fw-bold text-end">
            Target Count
          </th>
          <th class="py-3 text-uppercase text-muted small fw-bold text-end pe-4">
            Progress
          </th>
          <th class="py-3 text-uppercase text-muted small fw-bold">
            Status
          </th>
        </tr>
      </thead>
      <tbody>
        <template
          v-for="category in categories"
          :key="category.name"
        >
          <!-- Category Header Row -->
          <tr class="category-header">
            <td
              colspan="5"
              class="ps-4 py-2 fw-bold text-white bg-dark"
            >
              {{ category.label }}
            </td>
          </tr>
          
          <!-- Item Rows -->
          <tr 
            v-for="item in category.items" 
            :key="item.name" 
            class="data-row"
            :class="getStatusTheme(item.progress, item.status).rowClass"
          >
            <td class="ps-4 fw-semibold text-dark">
              {{ item.label }}
            </td>
            <td class="text-muted fw-medium text-end">
              {{ item.count.toLocaleString() }}
            </td>
            <td class="text-muted fw-medium text-end">
              {{ item.target.toLocaleString() }}
            </td>
            <td class="text-end pe-4">
              <ProgressBar
                :progress="item.progress"
                :status="item.status"
              />
            </td>
            <td>
              <StatusBadge
                :progress="item.progress"
                :status="item.status"
              />
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.table {
  background: white;
}
.category-header td {
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  background-color: #1a202c !important;
}
.data-row td {
  padding: 1rem 0.75rem;
  border-bottom: 1px solid #f7fafc;
}
.data-row:hover {
  background-color: #f8fafc;
}
.row-completed td {
  background-color: #f0fdf4 !important; /* Desaturated Green */
}
.row-near-completion td {
  background-color: #fffbeb !important; /* Desaturated Yellow/Orange */
}
.row-in-progress td {
  background-color: #ffffff !important; /* White */
}
thead th {
  border-top: none;
  border-bottom: 2px solid #edf2f7;
}
</style>
